import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";

interface SimpleListboxProps {
	id: string;
	prompt?: string;
	value: string | number | undefined;
	options: SimpleOption[];
	className?: string;
	onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

export interface SimpleOption {
	value: string | number;
	label: string;
	disabled?: boolean;
	color?: string;
}

export default function SimpleListbox({
	id,
	prompt,
	value,
	options,
	className,
	onChange,
}: SimpleListboxProps) {
	const selectedOption = options.find((opt) => opt.value == value) || null;

	// Trigger UI update when value is changed.
	const handleChange = (newValue: string) => {
		const event = {
			target: { value: newValue },
		} as unknown as React.ChangeEvent<HTMLSelectElement>;
		onChange(event);
	};

	return (
		<Listbox
			value={value != null ? String(value) : prompt}
			onChange={handleChange}
			disabled={options.length == 0}>
			<div className={`${className} relative`}>
				<ListboxButton
					id={id}
					className={`relative w-full h-9 cursor-default rounded-lg border border-gray-300 py-2 pl-3 pr-10 text-left text-sm text-gray-900 focus:border-primary-600 focus:outline-none focus:ring-1 focus:ring-primary-600`}>
					<span className="flex items-center truncate">
						{selectedOption?.color && (
							<span
								className={`inline-block w-3 h-3 rounded-full mr-2 ${selectedOption.color}`}></span>
						)}
						{selectedOption ? selectedOption.label : (prompt ?? options[0]?.label)}
					</span>
					<span className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
						<ChevronDownIcon className="h-5 w-5 text-gray-400" />
					</span>
				</ListboxButton>

				<ListboxOptions className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
					{prompt && (
						<ListboxOption
							key={prompt}
							value={prompt}
							disabled
							className="cursor-default select-none py-2 pl-3 pr-10 text-gray-400">
							{prompt}
						</ListboxOption>
					)}
					{options.map((option) => (
						<ListboxOption
							disabled={option.disabled ?? false}
							key={option.value}
							value={option.value}
							className={({ active }) =>
								`relative cursor-pointer select-none py-2 pl-3 pr-10 ${
									active ? "bg-primary-100 text-primary-900" : "text-gray-900"
								} aria-disabled:bg-slate-200/75 aria-disabled:cursor-default aria-disabled:text-gray-400`
							}>
							{({ selected }) => (
								<>
									<span
										className={`flex items-center ${
											selected ? "font-medium" : "font-normal"
										}`}>
										{option.color && (
											<span
												className={`inline-block w-3 h-3 rounded-full mr-2 ${option.color}`}></span>
										)}
										{option.label}
									</span>
								</>
							)}
						</ListboxOption>
					))}
				</ListboxOptions>
			</div>
		</Listbox>
	);
}
